/**
 * client/src/lib/webauthn/platform-authenticator-availability.ts — is there a
 * built-in authenticator (Windows Hello / Touch ID) on THIS device?
 *
 * WHY A CACHE, AND WHY IT IS READ SYNCHRONOUSLY
 * --------------------------------------------
 * `PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()` is async
 * and, on Windows, is not instant — it talks to the platform authenticator
 * stack. The one place that needs the answer (`preferLocalCredential`) sits on
 * the click path between "Sign in" and `navigator.credentials.get()`, which is
 * the exact stretch of dead air ledger #78/#88 exist to shorten. Awaiting a
 * capability query there would spend part of what those entries bought.
 *
 * So the query is fired EARLY (`primePlatformAuthenticatorAvailability`, called
 * when the sign-in form mounts) and the click path reads the settled answer
 * synchronously. An unsettled or failed probe reads as `false` — "not known to
 * be available" — so every consumer degrades to the behaviour it had before
 * this module existed. It is a latency hint, never a gate: nothing here decides
 * whether a ceremony may run, only how the browser is asked to route it.
 *
 * The answer is a property of the device, not of the account or the session, so
 * caching it for the page's lifetime cannot leak anything between accounts and
 * cannot go stale in any way that matters — a machine does not grow a
 * fingerprint reader mid-session.
 */

import type { PublicKeyCredentialDescriptorJSON } from "@simplewebauthn/browser";

/** A credential reachable only over `internal` needs no chooser and no radio. */
export function isInternalOnly(descriptor: PublicKeyCredentialDescriptorJSON): boolean {
  const transports = descriptor.transports;
  return Array.isArray(transports)
    && transports.length > 0
    && transports.every((t) => t === "internal");
}

/** Could this credential live in the built-in authenticator? True when it
 *  advertises `internal` at all — a Chrome-synced passkey reports
 *  `["internal","hybrid"]`, the exact case the hint is for. No transports at
 *  all is unknown, not internal, so it never justifies the hint on its own. */
export function isInternalCapable(descriptor: PublicKeyCredentialDescriptorJSON): boolean {
  return Array.isArray(descriptor.transports) && descriptor.transports.includes("internal");
}

/** null = not yet known (unprimed, in flight, or the probe failed). */
let known: boolean | null = null;
let inFlight = false;

/**
 * Start the capability probe if it has not run. Fire-and-forget: never throws,
 * never returns anything, and a browser that lacks the API simply leaves the
 * answer unknown.
 */
export function primePlatformAuthenticatorAvailability(): void {
  if (known !== null || inFlight) return;
  const api = typeof window !== "undefined"
    ? (window.PublicKeyCredential as typeof PublicKeyCredential | undefined)
    : undefined;
  if (!api?.isUserVerifyingPlatformAuthenticatorAvailable) return;
  inFlight = true;
  api.isUserVerifyingPlatformAuthenticatorAvailable().then(
    (available) => { known = available === true; inFlight = false; },
    () => { inFlight = false; /* unknown stays unknown — see header */ },
  );
}

/**
 * True ONLY when the probe has settled and said yes. Unprimed, in flight, or
 * failed all read false, so a caller can never act on a guess.
 */
export function platformAuthenticatorKnownAvailable(): boolean {
  return known === true;
}

/** Test-only: forget the cached answer so a case can prime it afresh. */
export function resetPlatformAuthenticatorAvailabilityForTests(): void {
  known = null;
  inFlight = false;
}
